import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";

import { previewOf } from "../voice/transcript-text.ts";

/**
 * ===========================================================================
 * LA CAJA DE ESCRIBIR, CON VISTA PREVIA EN CURSIVA
 * ===========================================================================
 *
 * EL PROBLEMA: un `<textarea>` no puede llevar una parte de su texto en
 * cursiva. Su contenido es texto plano; no admite hijos ni marcado. Y la vista
 * previa del dictado pide exactamente eso -- lo confirmado normal, lo
 * provisional en cursiva y atenuado, uno detras de otro y en el mismo parrafo.
 *
 * LAS DOS SALIDAS, y por que se elige la primera:
 *
 * 1. CAPA ESPEJO (esto). El `<textarea>` se queda intacto y detras se dibuja
 *    un `<div>` que repite el texto: la parte confirmada INVISIBLE --ocupa su
 *    sitio pero no se ve, porque ya la pinta el textarea-- y la provisional en
 *    cursiva, justo donde acabaria. El textarea tiene el fondo transparente,
 *    asi que la cursiva se ve a traves.
 *
 * 2. CONTENTEDITABLE. Da estilos de verdad, y se lleva por delante cosas que
 *    hoy funcionan solas: el deshacer del navegador, la composicion del IME
 *    --tildes y dieresis con teclas muertas, que en español no es un caso
 *    raro-- y el `value` controlado sin que el cursor salte. Ademas obliga a
 *    sanear lo que se pega, porque llega como HTML.
 *
 * Se elige la capa espejo por DONDE CAE EL RIESGO de cada una. Lo que puede
 * salir mal aqui es visual --que la cursiva no cuadre con el texto-- y se ve
 * de un vistazo, solo pasa mientras se dicta y no impide escribir. Lo que
 * puede salir mal con contenteditable afecta a ESCRIBIR, que es lo que el
 * usuario hace el 100% del tiempo, incluyendo a quien no dicta nunca. Cambiar
 * el modo de entrada de todo el chat para poner una palabra en cursiva es
 * pagar en la funcion principal por una secundaria.
 *
 * COMO SE EVITA QUE EL TEXTO BAILE, que es el riesgo real de esta opcion:
 *
 * - Las metricas no se escriben dos veces. La capa y el textarea comparten la
 *   MISMA clase (`FIELD`) para caja, ajuste de linea y partido de palabra. No
 *   se puede cambiar una y olvidar la otra porque son la misma cadena.
 * - Ni el tipo ni el tamaño de letra se declaran: los dos heredan del mismo
 *   padre. Declararlos seria abrir la puerta a que se separen.
 * - QUIEN SCROLLEA ES EL CONTENEDOR, no el textarea. Aqui las barras ocupan
 *   ancho de verdad (10 px, ver `scrollbars.css`), asi que un textarea con
 *   scroll propio tendria 10 px menos de ancho util que la capa y el texto se
 *   partiria en sitios distintos. Con el scroll en el contenedor, los dos
 *   miden lo mismo siempre y ademas se mueven juntos sin sincronizar nada.
 * - El textarea crece hasta su altura completa y no se le pone tope: el tope
 *   lo pone el contenedor.
 */
export function DictationField({
  value,
  interim,
  disabled,
  placeholder,
  textareaRef,
  onChange,
  onKeyDown,
}: {
  value: string;
  /** Texto provisional del dictado. Cadena vacia cuando no hay. */
  interim: string;
  disabled: boolean;
  placeholder: string;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  onChange: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}) {
  const preview = previewOf(value, interim);
  const dictating = preview.provisional !== "";
  const announced = useAnnouncedText(preview.provisional);
  const mirror = useRef<HTMLDivElement>(null);

  // La caja crece tambien cuando cambia lo provisional, que no pasa por
  // `onChange`: sin esto, dictar un parrafo largo lo dejaria cortado.
  useEffect(() => {
    if (textareaRef.current !== null) {
      grow(textareaRef.current, mirror.current);
    }
  }, [value, interim, textareaRef]);

  return (
    <div
      className={`scroll-quiet relative max-h-48 min-w-0 flex-1 overflow-y-auto ${
        disabled ? "opacity-60" : ""
      }`}
    >
      {/* La capa espejo. `aria-hidden` porque es un duplicado visual: lo que
          un lector de pantalla tiene que leer va abajo, en su region. */}
      {dictating && (
        <div
          ref={mirror}
          className={`${FIELD} pointer-events-none absolute inset-x-0 top-0`}
          aria-hidden="true"
        >
          {/* Invisible, no ausente: reserva exactamente el hueco que ocupa el
              texto que el propio textarea ya esta pintando encima. */}
          <span className="invisible">{preview.confirmed}</span>
          <span className="text-muted-foreground italic">
            {preview.provisional}
          </span>
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={value}
        rows={1}
        // Sin placeholder mientras se dicta: con el valor vacio el navegador
        // lo pinta igual, y la capa espejo le dibuja la vista previa encima.
        placeholder={dictating ? "" : placeholder}
        disabled={disabled}
        onKeyDown={onKeyDown}
        aria-describedby={dictating ? DICTATION_HINT_ID : undefined}
        className={`${FIELD} relative block w-full resize-none overflow-hidden bg-transparent outline-none`}
        onChange={(event) => {
          onChange(event.target.value);
          grow(event.target, mirror.current);
        }}
      />

      {/**
       * Para quien no ve la cursiva.
       *
       * Dos cosas distintas: el aviso de QUE hay texto sin confirmar --que la
       * cursiva no sea la unica señal, porque no todo el mundo la percibe-- y
       * la region que va leyendo lo dictado.
       *
       * Se anuncia cada cierto tiempo y solo lo nuevo, no cada mensaje del
       * servidor: el texto provisional cambia varias veces por segundo y llega
       * acumulado, asi que leerlo tal cual seria repetir la frase entera una y
       * otra vez.
       */}
      <span id={DICTATION_HINT_ID} className="sr-only">
        El texto en cursiva al final todavía no es definitivo: se está dictando.
      </span>
      <div className="sr-only" role="status" aria-live="polite">
        {announced}
      </div>
    </div>
  );
}

const DICTATION_HINT_ID = "dictation-hint";

/**
 * Las metricas compartidas por el textarea y su espejo.
 *
 * Una sola cadena a proposito: si esto fueran dos listas de clases parecidas,
 * la proxima vez que alguien ajuste el espaciado cambiaria una y el texto
 * empezaria a bailar sin que nadie supiera por que.
 *
 * `whitespace-pre-wrap` y `break-words` reproducen lo que un textarea hace por
 * defecto; el tipo y el tamaño de letra NO se declaran, se heredan del mismo
 * padre.
 */
const FIELD = "px-0 py-2 break-words whitespace-pre-wrap";

/**
 * Estira la caja hasta que quepa TODO lo que se va a ver.
 *
 * El alto lo marca el mayor de los dos: lo escrito --que mide el textarea-- y
 * la vista previa --que mide el espejo--. El espejo esta posicionado en
 * absoluto, asi que no empuja nada por si solo: si el alto saliera solo del
 * textarea, dictar un parrafo lo dejaria cortado por abajo, porque lo
 * provisional NO esta dentro del textarea. Se vio en un navegador de verdad;
 * en jsdom no hay maquetacion y ninguna prueba podia enseñarlo.
 *
 * Sin tope: el alto lo limita el contenedor, que es quien scrollea. Poner aqui
 * otro tope seria tener dos, y el texto se cortaria antes de tiempo.
 */
const grow = (
  element: HTMLTextAreaElement,
  mirror: HTMLDivElement | null,
): void => {
  element.style.height = "auto";
  const needed = Math.max(element.scrollHeight, mirror?.offsetHeight ?? 0);
  element.style.height = `${String(needed)}px`;
};

/**
 * Cada cuanto se le cuenta al lector de pantalla lo que va saliendo.
 *
 * No es "cuando el texto deje de cambiar": el servidor manda un mensaje cada
 * decima de segundo mientras se habla, asi que esperar a que se pare significa
 * no anunciar NADA en todo el dictado. Se probo asi y la region se quedaba
 * vacia de principio a fin.
 */
const ANNOUNCE_INTERVAL_MS = 1600;

/**
 * Lo que hay que leer en voz alta, cada tanto y SOLO LO NUEVO.
 *
 * Lo nuevo y no el texto entero porque el servidor manda lo acumulado: leer
 * cada version completa haria que el lector repitiera la frase desde el
 * principio una y otra vez, que es peor que no decir nada. Si el modelo
 * CORRIGIO lo que ya habia dicho --puede hacerlo al oir mas contexto-- ya no
 * es una continuacion, y entonces si se lee entera.
 */
const useAnnouncedText = (text: string): string => {
  const [announced, setAnnounced] = useState("");
  const latest = useRef(text);
  const said = useRef("");
  latest.current = text;

  const dictating = text !== "";

  useEffect(() => {
    if (!dictating) {
      said.current = "";
      setAnnounced("");
      return;
    }

    const timer = setInterval(() => {
      const now = latest.current;
      if (now === said.current) return;

      const fresh = now.startsWith(said.current)
        ? now.slice(said.current.length)
        : now;
      said.current = now;
      setAnnounced(fresh.trim());
    }, ANNOUNCE_INTERVAL_MS);

    return () => {
      clearInterval(timer);
    };
  }, [dictating]);

  return announced;
};
